import { clearAuthHeaders } from '../config';
import authSvc from './authSvc';
import authUtils from './authUtils';
import cognitoService from './cognitoService';
import storageService from './storageService';
import { AuthCredentials } from './types';

/**
 * Login with email and password
 * @param credentials - Email and password
 * @returns Promise with auth response
 */
export const login = async (credentials: AuthCredentials): Promise<any> => {
  if (authUtils.isLegacyAuth()) {
    return authSvc.login(credentials);
  }
  const cognitoResponse = await authSvc.cognitoLogin(credentials);
  return cognitoService.processLoginResponse(cognitoResponse);
};

/**
 * Logout user from the current auth system
 * @returns Promise with logout response
 */
export const logout = async (): Promise<any> => {
  if (authUtils.isLegacyAuth()) {
    const refreshToken = authUtils.getRefreshToken();
    if (refreshToken) {
      await authSvc.tokenBlacklist({ refresh: refreshToken });
    }
  } else {
    const accessToken = authUtils.getAccessToken();
    if (accessToken) {
      await authSvc.cognitoLogout(accessToken);
    }
  }

  authUtils.clearAuthData();

  // Clear Axios auth headers
  clearAuthHeaders();
};

/**
 * Refresh authentication token
 * @returns Promise with refreshed token response
 */
export const refreshToken = async (): Promise<any> => {
  const currentRefreshToken = authUtils.getRefreshToken();
  if (!currentRefreshToken) {
    throw new Error('No refresh token available');
  }

  if (authUtils.isLegacyAuth()) {
    return authSvc.tokenRefresh({ refresh: currentRefreshToken });
  }

  const cognitoUuid = storageService.getCognitoUuid();
  if (!cognitoUuid) {
    throw new Error('No cognitoUuid available for token refresh');
  }

  const response = await authSvc.cognitoTokenRefresh({
    refreshToken: currentRefreshToken,
    cognitoUuid,
  });
  if (response.idToken) {
    cognitoService.handleTokenRefresh(response.idToken);
  }
  return response;
};

export default {
  login,
  logout,
  refreshToken,
};
